import {
  filterInformeRows,
  InformeField,
  InformeFieldType,
  InformeFilter,
  InformeFilterOperator,
} from './informeDinamicoUtils';

export type CampoFiltroVentaId =
  | 'cliente'
  | 'proveedor'
  | 'articulo'
  | 'comprobante'
  | 'tipo'
  | 'sucursal'
  | 'ubicacion'
  | 'vendedor'
  | 'formaPago'
  | 'localidad'
  | 'ramo'
  | 'linea'
  | 'clase'
  | 'usuario'
  | 'cantidad'
  | 'total';

export interface CampoFiltroVenta {
  id: CampoFiltroVentaId;
  label: string;
  kind: 'cliente' | 'articulo' | 'texto' | 'lista' | 'localidad' | 'catalogo' | 'numero';
}

const CAMPOS_FILTRO_COMUNES: CampoFiltroVenta[] = [
  { id: 'articulo', label: 'Artículo', kind: 'articulo' },
  { id: 'comprobante', label: 'Comprobante', kind: 'texto' },
  { id: 'tipo', label: 'Tipo de comprobante', kind: 'lista' },
  { id: 'sucursal', label: 'Sucursal', kind: 'lista' },
  { id: 'ubicacion', label: 'Ubicación', kind: 'lista' },
  { id: 'vendedor', label: 'Vendedor', kind: 'lista' },
  { id: 'formaPago', label: 'Forma de pago', kind: 'lista' },
  { id: 'localidad', label: 'Localidad', kind: 'localidad' },
  { id: 'ramo', label: 'Ramo', kind: 'catalogo' },
  { id: 'linea', label: 'Línea', kind: 'catalogo' },
  { id: 'clase', label: 'Clase', kind: 'catalogo' },
  { id: 'usuario', label: 'Usuario', kind: 'lista' },
  { id: 'cantidad', label: 'Cantidad', kind: 'numero' },
  { id: 'total', label: 'Importe total', kind: 'numero' },
];

export type ModuloFiltroInforme = 'Ventas' | 'Compras';
export type ModuloPanelFiltros = ModuloFiltroInforme | 'Contabilidad' | 'Tesoreria';

export function filtraPorColumnasDelInforme(modulo: string): boolean {
  return modulo === 'Contabilidad' || modulo === 'Tesoreria';
}

export function operadorAlElegirCampoColumna(tipo: InformeFieldType): InformeFilterOperator {
  return tipo === 'text' ? 'contains' : 'greaterThan';
}

export function filtroAdicionalColumnaNuevo(
  id: number,
  fieldId: string,
  tipo: InformeFieldType,
): InformeFilter {
  return {
    id,
    fieldId,
    operator: operadorAlElegirCampoColumna(tipo),
    value: '',
    valorHasta: '',
  };
}

export function camposFiltroInforme(modulo: ModuloFiltroInforme): CampoFiltroVenta[] {
  const entidad: CampoFiltroVenta = modulo === 'Ventas'
    ? { id: 'cliente', label: 'Cliente', kind: 'cliente' }
    : { id: 'proveedor', label: 'Proveedor', kind: 'cliente' };
  return [entidad, ...CAMPOS_FILTRO_COMUNES];
}

export const CAMPOS_FILTRO_VENTA: CampoFiltroVenta[] = camposFiltroInforme('Ventas');

const IDS_FILTRO_VENTA = new Set<string>([
  ...camposFiltroInforme('Ventas').map((campo) => campo.id),
  ...camposFiltroInforme('Compras').map((campo) => campo.id),
]);

export interface ValorCliente {
  nro: string;
  nombre: string;
}

export interface ValorArticulo {
  codigo: string;
  descripcion: string;
}

export interface ValorLocalidad {
  ciudad: string;
  provincia: string;
}

export interface ValorVendedor {
  nro: string;
  nombre: string;
}

export function esCampoFiltroVenta(fieldId: string): fieldId is CampoFiltroVentaId {
  return IDS_FILTRO_VENTA.has(fieldId);
}

export function admiteVariosValores(operator: InformeFilterOperator): boolean {
  return operator === 'equals' || operator === 'contains';
}

export function leerListaValores(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === 'string' || typeof item === 'number' ? String(item).trim() : ''))
          .filter(Boolean);
      }
    } catch {
      return [trimmed];
    }
  }
  return [trimmed];
}

export function guardarListaValores(valores: string[]): string {
  const limpios: string[] = [];
  valores.forEach((valor) => {
    const texto = valor.trim();
    if (!texto || limpios.some((item) => item.localeCompare(texto, 'es', { sensitivity: 'accent' }) === 0)) return;
    limpios.push(texto);
  });
  if (limpios.length <= 1) return limpios[0] ?? '';
  return JSON.stringify(limpios);
}

export function agruparFiltrosMismoCampo<T extends { fieldId: string; operator: InformeFilterOperator; value: string }>(
  filtros: T[],
): T[] {
  const resultado: T[] = [];
  const indice = new Map<string, number>();
  filtros.forEach((filtro) => {
    if (!admiteVariosValores(filtro.operator) || esCampoFiltroVenta(filtro.fieldId)) {
      resultado.push(filtro);
      return;
    }
    const clave = `${filtro.fieldId}\0${filtro.operator}`;
    const existente = indice.get(clave);
    if (existente == null) {
      indice.set(clave, resultado.length);
      resultado.push({ ...filtro, value: guardarListaValores(leerListaValores(filtro.value)) });
      return;
    }
    const previo = resultado[existente];
    resultado[existente] = {
      ...previo,
      value: guardarListaValores([
        ...leerListaValores(previo.value),
        ...leerListaValores(filtro.value),
      ]),
    };
  });
  return resultado;
}

export function operadorAlElegirCampoVenta(fieldId: string): InformeFilterOperator {
  if (fieldId === 'comprobante') return 'equals';
  if (fieldId === 'cantidad' || fieldId === 'total') return 'greaterThan';
  return 'equals';
}

export function muestraCondicionFiltroVenta(fieldId: string): boolean {
  return fieldId === 'comprobante' || fieldId === 'cantidad' || fieldId === 'total';
}

export function filtroAdicionalVentaNuevo(id: number, modulo: ModuloFiltroInforme = 'Ventas'): InformeFilter {
  return {
    id,
    fieldId: modulo === 'Ventas' ? 'cliente' : 'proveedor',
    operator: 'equals',
    value: '',
    valorHasta: '',
  };
}

function unpack(value: string): Record<string, string> | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const data: Record<string, string> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([key, item]) => {
      if (typeof item === 'string' || typeof item === 'number') data[key] = String(item).trim();
    });
    return data;
  } catch {
    return null;
  }
}

function pack(data: Record<string, string>): string {
  if (Object.values(data).every((item) => !item.trim())) return '';
  return JSON.stringify(data);
}

export function leerCliente(value: string): ValorCliente {
  const data = unpack(value);
  if (!data) return { nro: '', nombre: value.trim() };
  return { nro: data.nro ?? '', nombre: data.nombre ?? '' };
}

export function guardarCliente(valor: ValorCliente): string {
  return pack({ nro: valor.nro.trim(), nombre: valor.nombre.trim() });
}

export function etiquetaCliente(valor: ValorCliente): string {
  return [valor.nro, valor.nombre].filter(Boolean).join(' - ');
}

export function leerArticulo(value: string): ValorArticulo {
  const data = unpack(value);
  if (!data) return { codigo: '', descripcion: value.trim() };
  return { codigo: data.codigo ?? '', descripcion: data.descripcion ?? '' };
}

export function guardarArticulo(valor: ValorArticulo): string {
  return pack({ codigo: valor.codigo.trim(), descripcion: valor.descripcion.trim() });
}

export function etiquetaArticulo(valor: ValorArticulo): string {
  return [valor.codigo, valor.descripcion].filter(Boolean).join(' — ');
}

export function leerLocalidad(value: string): ValorLocalidad {
  const data = unpack(value);
  if (!data) return { ciudad: '', provincia: value.trim() };
  return { ciudad: data.ciudad ?? '', provincia: data.provincia ?? '' };
}

export function guardarLocalidad(valor: ValorLocalidad): string {
  return pack({ ciudad: valor.ciudad.trim(), provincia: valor.provincia.trim() });
}

export function leerVendedor(value: string): ValorVendedor {
  const data = unpack(value);
  if (!data) return { nro: '', nombre: value.trim() };
  return { nro: data.nro ?? '', nombre: data.nombre ?? '' };
}

export function guardarVendedor(valor: ValorVendedor): string {
  return pack({ nro: valor.nro.trim(), nombre: valor.nombre.trim() });
}

export function migrarFiltrosAdicionalesVentas(
  filtros: InformeFilter[],
  modulo: ModuloFiltroInforme = 'Ventas',
): InformeFilter[] {
  const entidadId = modulo === 'Ventas' ? 'cliente' : 'proveedor';
  return filtros.map((filtro) => {
    if (filtro.fieldId === 'entidad' || filtro.fieldId === 'entidadNumero' || filtro.fieldId === 'cliente' || filtro.fieldId === 'proveedor') {
      const empaquetado = unpack(filtro.value);
      if (empaquetado && (empaquetado.nro != null || empaquetado.nombre != null)) {
        return filtro.fieldId === 'cliente' || filtro.fieldId === 'proveedor'
          ? { ...filtro, fieldId: entidadId }
          : { ...filtro, fieldId: entidadId, operator: 'equals' as const };
      }
      if (filtro.fieldId === 'entidadNumero') {
        return {
          ...filtro,
          fieldId: entidadId,
          operator: 'equals',
          value: guardarCliente({ nro: filtro.value, nombre: '' }),
        };
      }
      if (filtro.fieldId === 'cliente' || filtro.fieldId === 'proveedor') {
        return { ...filtro, fieldId: entidadId };
      }
      return {
        ...filtro,
        fieldId: entidadId,
        operator: filtro.operator === 'contains' ? 'contains' : 'equals',
        value: guardarCliente({ nro: '', nombre: filtro.value }),
      };
    }
    if (filtro.fieldId === 'articuloCodigo' || filtro.fieldId === 'articulo') {
      const empaquetado = unpack(filtro.value);
      if (empaquetado && (empaquetado.codigo != null || empaquetado.descripcion != null)) {
        return { ...filtro, fieldId: 'articulo' };
      }
      if (filtro.fieldId === 'articuloCodigo') {
        return {
          ...filtro,
          fieldId: 'articulo',
          operator: 'equals',
          value: guardarArticulo({ codigo: filtro.value, descripcion: '' }),
        };
      }
      return {
        ...filtro,
        fieldId: 'articulo',
        operator: filtro.operator === 'contains' ? 'contains' : 'equals',
        value: guardarArticulo({ codigo: '', descripcion: filtro.value }),
      };
    }
    if (filtro.fieldId === 'ciudad' || filtro.fieldId === 'provincia') {
      const empaquetado = unpack(filtro.value);
      if (empaquetado && (empaquetado.ciudad != null || empaquetado.provincia != null)) {
        return { ...filtro, fieldId: 'localidad', operator: 'equals' as const };
      }
      if (filtro.fieldId === 'ciudad') {
        return {
          ...filtro,
          fieldId: 'localidad',
          operator: 'equals',
          value: guardarLocalidad({ ciudad: filtro.value, provincia: '' }),
        };
      }
      return {
        ...filtro,
        fieldId: 'localidad',
        operator: 'equals',
        value: guardarLocalidad({ ciudad: '', provincia: filtro.value }),
      };
    }
    if (filtro.fieldId === 'vendedor' && filtro.value.trim() && !unpack(filtro.value)) {
      return { ...filtro, value: guardarVendedor({ nro: '', nombre: filtro.value }) };
    }
    return filtro;
  });
}

function textoFila(row: object, keys: string[]): string {
  for (const key of keys) {
    const value = Reflect.get(row, key);
    if (value != null && String(value).trim() !== '') return String(value).trim();
  }
  return '';
}

function igual(left: string, right: string): boolean {
  return left.localeCompare(right, 'es', { sensitivity: 'accent' }) === 0;
}

function contiene(haystack: string, needle: string): boolean {
  const expected = needle.trim();
  if (!expected) return false;
  return haystack.toLocaleLowerCase('es').includes(expected.toLocaleLowerCase('es'));
}

function coincideTexto(actual: string, operator: InformeFilterOperator, expected: string): boolean {
  if (!expected.trim()) return true;
  if (!actual) return false;
  return operator === 'contains' ? contiene(actual, expected) : igual(actual, expected);
}

function coincideAlguno(valores: string[], operator: InformeFilterOperator, expected: string): boolean {
  return valores.some((actual) => coincideTexto(actual, operator, expected));
}

function filtroTieneValor(filtro: InformeFilter): boolean {
  if (filtro.operator === 'between') {
    return Boolean(filtro.value.trim() && filtro.valorHasta?.trim());
  }
  const data = unpack(filtro.value);
  if (data) return Object.values(data).some((item) => item.trim());
  return Boolean(filtro.value.trim());
}

/**
 * Números de cuenta de los filtros adicionales "Número de cuenta" que se pueden
 * aplicar en la consulta, antes del tope de filas.
 * [] = no hay filtro de cuenta. undefined = hay un filtro que no es igualdad exacta.
 */
export function cuentasImpNroCuentaParaConsulta(
  filters: Pick<InformeFilter, 'fieldId' | 'operator' | 'value' | 'valorHasta'>[],
): number[] | undefined {
  const activos = filters.filter((filtro) => filtro.fieldId === 'ImpNroCuenta' && filtroTieneValor(filtro as InformeFilter));
  if (activos.length === 0) return [];
  if (activos.some((filtro) => filtro.operator !== 'equals')) return undefined;
  const cuentas: number[] = [];
  for (const valor of activos.flatMap((filtro) => leerListaValores(filtro.value))) {
    const nro = Number(valor);
    if (!Number.isInteger(nro) || nro < 0) return undefined;
    if (!cuentas.includes(nro)) cuentas.push(nro);
  }
  return cuentas;
}

/** Cruza la cuenta del origen con las del filtro adicional. [] = la intersección está vacía. */
export function cuentasConsultaContabilidad(
  origen: number | undefined,
  adicionales: number[] | undefined,
): number[] | undefined {
  if (adicionales == null || adicionales.length === 0) {
    return origen != null ? [origen] : undefined;
  }
  if (origen == null) return adicionales;
  return adicionales.includes(origen) ? [origen] : [];
}

function coincideNumero(actual: unknown, filtro: InformeFilter): boolean {
  if (actual == null || actual === '') return false;
  const toNumber = (raw: unknown) => Number(raw);
  const actualNumber = toNumber(actual);
  const expectedNumber = toNumber(filtro.value);
  if (!Number.isFinite(actualNumber) || !Number.isFinite(expectedNumber)) return false;
  if (filtro.operator === 'between') {
    const endNumber = toNumber(filtro.valorHasta);
    if (!Number.isFinite(endNumber)) return false;
    return actualNumber >= expectedNumber && actualNumber <= endNumber;
  }
  if (filtro.operator === 'lessThan') return actualNumber <= expectedNumber;
  if (filtro.operator === 'equals') return actualNumber === expectedNumber;
  return actualNumber >= expectedNumber;
}

function coincideFiltroVenta(row: object, filtro: InformeFilter, fields: InformeField[]): boolean {
  if (filtro.fieldId === 'cliente' || filtro.fieldId === 'proveedor') {
    const cliente = leerCliente(filtro.value);
    const nro = textoFila(row, filtro.fieldId === 'proveedor' ? ['nroProveedor', 'nroCliente'] : ['nroCliente']);
    const nombre = textoFila(row, filtro.fieldId === 'proveedor' ? ['proNombre', 'nombre'] : ['cliNombre', 'nombre']);
    const porNro = Boolean(cliente.nro) && igual(nro, cliente.nro);
    const porNombre = Boolean(cliente.nombre) && coincideTexto(nombre, filtro.operator, cliente.nombre);
    if (cliente.nro && cliente.nombre) return porNro || porNombre;
    if (cliente.nro) return porNro;
    return porNombre;
  }
  if (filtro.fieldId === 'articulo') {
    const articulo = leerArticulo(filtro.value);
    const codigo = textoFila(row, ['iteCodigoArticulo', 'codigoArticulo']);
    const descripcion = textoFila(row, ['iteDescripcion', 'descripcion']);
    const porCodigo = Boolean(articulo.codigo) && igual(codigo, articulo.codigo);
    const porDescripcion = Boolean(articulo.descripcion)
      && coincideTexto(descripcion, filtro.operator, articulo.descripcion);
    if (articulo.codigo && articulo.descripcion) return porCodigo || porDescripcion;
    if (articulo.codigo) return porCodigo;
    return porDescripcion;
  }
  if (filtro.fieldId === 'localidad') {
    const localidad = leerLocalidad(filtro.value);
    if (localidad.ciudad) return igual(textoFila(row, ['ciuNombreCiudad']), localidad.ciudad);
    if (localidad.provincia) return igual(textoFila(row, ['pciaNombreProvincia']), localidad.provincia);
    return true;
  }
  if (filtro.fieldId === 'comprobante') {
    if (filtro.operator === 'contains') {
      return coincideAlguno(
        [textoFila(row, ['nroComprobante']), textoFila(row, ['nroFormulario'])].filter(Boolean),
        filtro.operator,
        filtro.value,
      );
    }
    const numero = textoFila(row, ['nroComprobante']) || textoFila(row, ['nroFormulario']);
    return coincideNumero(numero, filtro);
  }
  if (filtro.fieldId === 'tipo') {
    return coincideAlguno(
      [textoFila(row, ['nroTipoFormulario']), textoFila(row, ['tipDescripcion'])],
      filtro.operator,
      filtro.value,
    );
  }
  if (filtro.fieldId === 'sucursal') {
    return igual(textoFila(row, ['nroSuc']), filtro.value.trim());
  }
  if (filtro.fieldId === 'ubicacion') {
    return igual(textoFila(row, ['nroUbicacion']), filtro.value.trim());
  }
  if (filtro.fieldId === 'vendedor') {
    const vendedor = leerVendedor(filtro.value);
    const nro = textoFila(row, ['forNroVendedor']);
    const nombre = textoFila(row, ['venNombreVendedor']);
    const porNro = Boolean(vendedor.nro) && igual(nro, vendedor.nro);
    const porNombre = Boolean(vendedor.nombre) && coincideTexto(nombre, filtro.operator, vendedor.nombre);
    if (vendedor.nro && vendedor.nombre) return porNro || porNombre;
    if (vendedor.nro) return porNro;
    return porNombre;
  }
  if (filtro.fieldId === 'formaPago') {
    return coincideTexto(textoFila(row, ['formaDePago']), filtro.operator, filtro.value);
  }
  if (filtro.fieldId === 'usuario') {
    return coincideTexto(textoFila(row, ['usuario']), filtro.operator, filtro.value);
  }
  if (filtro.fieldId === 'ramo') {
    return coincideTexto(textoFila(row, ['nombreRamo']), filtro.operator, filtro.value);
  }
  if (filtro.fieldId === 'linea') {
    return coincideTexto(textoFila(row, ['nombreLinea']), filtro.operator, filtro.value);
  }
  if (filtro.fieldId === 'clase') {
    return coincideTexto(textoFila(row, ['nombreClase']), filtro.operator, filtro.value);
  }
  if (filtro.fieldId === 'cantidad' || filtro.fieldId === 'total') {
    const actual = filtro.fieldId === 'cantidad' ? Reflect.get(row, 'iteCantidad') : Reflect.get(row, 'importeTotal');
    return coincideNumero(actual, filtro);
  }
  const lista = admiteVariosValores(filtro.operator) ? leerListaValores(filtro.value) : [];
  if (lista.length > 1) {
    return lista.some((valor) => filterInformeRows([row], [{ ...filtro, value: valor }], fields).length === 1);
  }
  return filterInformeRows([row], [filtro], fields).length === 1;
}

export function filtrarFilasInformeVentas(
  rows: object[],
  filters: InformeFilter[],
  fields: InformeField[],
): object[] {
  const active = agruparFiltrosMismoCampo(filters.filter(filtroTieneValor));
  return rows.filter((row) => active.every((filtro) => coincideFiltroVenta(row, filtro, fields)));
}
